import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { InterventionStore } from '../../../intervention.store';
import { InterventionConsentDecision, InterventionConsentResponse, InterventionConsentType } from '../../../intervention.model';
import { toDateOnlyString } from '@shared/utils/date-utils';
import { nullIfBlank } from '@shared/utils/payload-utils';

// Referral consent (BR004) is recorded by internal staff before a referral; service consent (BR005) by the accepting
// provider, or staff, before service is delivered. The server decides who may do what; this panel hides what the
// caller can't use. Granting again adds a new version; only the version in force can be withdrawn.
@Component({
  selector: 'intervention-consents-panel',
  imports: [DatePipe, ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './intervention-consents-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionConsentsPanelComponent {
  protected readonly interventionStore = inject(InterventionStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);

  protected readonly types: { type: InterventionConsentType; label: string }[] = [
    { type: 'Referral', label: 'Consent to receive counselling (referral consent)' },
    { type: 'Service', label: 'Service consent' },
    { type: 'Disclosure', label: 'Disclosure consent' },
  ];

  // Referral consent is internal: a provider is not shown a row that would read "not recorded" when it is in force.
  protected readonly visibleTypes = computed(() =>
    this.types.filter((item) => item.type === 'Service' || this.permissionService.hasPermission(Permission.InterventionsUpdate)),
  );

  // The consent script the member is read: the lists are served in sort order, so the current version is the last item. Its metadata holds the wording that is
  // read (script) and what a member who refuses is told (refusedMessage). The version read is recorded with the consent, as proof of the wording.
  protected readonly script = computed(() => {
    const current = this.lookupStore.items(LOOKUP_CODES.consentScript).at(-1);
    if (!current) {
      return null;
    }

    const metadata = current.metadata ? (JSON.parse(current.metadata) as { script?: string; refusedMessage?: string }) : {};
    return { id: current.id, script: metadata.script ?? '', refusedMessage: metadata.refusedMessage ?? '' };
  });

  // The type being recorded (form open) and the consent being withdrawn (reason box open).
  protected readonly recordingType = signal<InterventionConsentType | null>(null);
  protected readonly withdrawingId = signal<string | null>(null);

  protected readonly form = this.formBuilder.nonNullable.group({
    // Granted or Declined; a refusal flags the case and does not close it
    decision: this.formBuilder.nonNullable.control<InterventionConsentDecision>('Granted'),
    givenBy: [''],
    scope: [''],
    expiryDate: this.formBuilder.control<Date | null>(null),
  });
  protected readonly withdrawReason = this.formBuilder.nonNullable.control('');

  private readonly closed = computed(() => this.interventionStatus() === 'Closed');

  // Referral consent is internal; the intervention-edit permission is what separates staff from providers.
  protected readonly canRecord = computed(() => ({
    Referral: !this.closed() && this.permissionService.hasPermission(Permission.InterventionsUpdate),
    Service: !this.closed() && this.permissionService.hasPermission(Permission.ConsentsRecord),
    Disclosure: !this.closed() && this.permissionService.hasPermission(Permission.InterventionsUpdate),
  }));

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.consentScript]);
  }

  protected open(type: InterventionConsentType): void {
    this.form.reset({ decision: 'Granted', givenBy: '', scope: '', expiryDate: null });
    this.withdrawingId.set(null);
    this.recordingType.set(type);
  }

  protected closeForm(): void {
    this.recordingType.set(null);
  }

  protected save(): void {
    const type = this.recordingType();
    if (!type) {
      return;
    }

    const value = this.form.getRawValue();
    this.interventionStore.grantConsent(
      this.interventionId(),
      {
        consentType: type,
        decision: value.decision,
        consentScriptItemId: this.script()?.id ?? null,
        givenBy: nullIfBlank(value.givenBy),
        scope: nullIfBlank(value.scope),
        expiryDate: value.decision === 'Granted' && value.expiryDate ? toDateOnlyString(value.expiryDate) : null,
      },
      () => this.closeForm(),
    );
  }

  protected openWithdraw(consent: InterventionConsentResponse): void {
    this.withdrawReason.reset('');
    this.recordingType.set(null);
    this.withdrawingId.set(consent.id);
  }

  protected closeWithdraw(): void {
    this.withdrawingId.set(null);
  }

  protected withdraw(consent: InterventionConsentResponse): void {
    const reason = this.withdrawReason.value.trim();
    if (!reason) {
      this.withdrawReason.markAsTouched();
      return;
    }

    this.interventionStore.withdrawConsent(this.interventionId(), consent.id, reason, () => this.closeWithdraw());
  }

  protected label(type: InterventionConsentType): string {
    return this.types.find((item) => item.type === type)?.label ?? type;
  }
}
